// programmeModel — the DERIVED read model the Dashboard reports against
// (007-dashboard-shell, Slice 1; north-star §6). It encodes the programme
// model — the Frame gate, the per-stream parallel phases, and the §6
// completion criteria — as a PURE projection over the underlying facts.
//
// Derive, don't store (spec FR-004, SC-002): every figure here — per-stream
// completion, the confidence-weighted overall progress, the estate value
// roll-up, the derived "recommended next" list — is computed on demand from
// the input facts. There is no persisted aggregate; the same facts always
// yield a deeply-equal result and the input is never mutated.
//
// Confidence-weighted honesty (FR-002/FR-003, SC-001): overall progress is
// NOT a raw phase ratio. Each stream's structural completion is weighted by
// the confidence we hold in that stream's facts, so thin/low-confidence data
// can never read 100% — low confidence pulls the figure down even when the
// structure looks advanced.

import {
  ILLUSTRATIVE_FORECAST_ACCURACY,
  ILLUSTRATIVE_VALUE_REALISATION,
  ILLUSTRATIVE_EXPECTED_MONEY,
  ILLUSTRATIVE_EXPECTED_ASSUMPTION,
  ILLUSTRATIVE_SPECULATIVE_MONEY,
  ILLUSTRATIVE_SPECULATIVE_ASSUMPTION,
  ILLUSTRATIVE_STREAM_COLUMNS,
  ILLUSTRATIVE_CALIBRATION,
} from './illustrative';

// Provenance/source vocabulary (029-value-proven-dashboard, FR-001). Echoes
// VisionBoard's `type Provenance = 'stated' | 'inferred'`, extended with
// `illustrative` (alias `sample`) as a FIRST-CLASS source so the badge derives
// structurally from the model, never from presentation. `measured` is NOT added
// yet — it lands with E6 (spec Key-Entities note).
export type Provenance = 'stated' | 'inferred' | 'illustrative';

export type StreamPhase = 'frame' | 'as-is-capture' | 'analysis' | 'to-be' | 'validate';

// Programme-model status (§6), derived from the phase + completion criteria.
// This is the at-a-glance "how far" label shown on a stream card.
export type StreamStatus =
  | 'not-started'
  | 'in-capture'
  | 'in-analysis'
  | 'in-validation'
  | 'signed-off';

export type VisionSummary = {
  /** The Cover Story headline/quote, in the sponsor's own words. */
  coverStory: string;
  /** Who stated it (provenance), e.g. "sponsor". */
  statedBy: string;
};

/** An assigned subject-matter expert (per area). */
export type AreaSME = {
  name: string;
  /** Email or role. */
  contact?: string;
};

/**
 * An "Area of the business" (the leadership-level scope unit, 023). ONE flat
 * list; the word is "Area" — never "domain"/"value stream". An area with >=1
 * SME is the unlock-bearing unit for the Frame gate (FR-004). The shape leaves
 * room for value-streams-within-areas and an Area tag LATER (FR-011) without a
 * re-model; neither is built here (no streams/vsm/domain hung off an Area).
 */
export type AreaFacts = {
  id: string;
  name: string;
  oneLiner?: string;
  smes: AreaSME[];
};

/** The five cockpit phases (§6 mapped into the dashboard buckets). */
export type PhaseId = 'setup' | 'frame' | 'map' | 'decide' | 'handover';

export type ChecklistState = 'done' | 'in-progress' | 'blocked' | 'locked' | 'todo';

export type ChecklistItem = {
  id: string;
  phase: PhaseId;
  label: string;
  required: boolean;
  state: ChecklistState;
  blockedReason?: string;
};

export type PhaseReadModel = {
  id: PhaseId;
  label: string;
  /** Required items that read "done" (whole count). */
  requiredDone: number;
  /** Count of required checklist items in this phase. */
  requiredTotal: number;
  /** 0..1, required-only, confidence-weighted for AI-derived (Map/Decide). */
  progress: number;
  /** true while the Frame gate is unmet (map/decide/handover); never setup/frame. */
  locked: boolean;
};

/** The Frame-gate facts that drive the "unlock mapping" momentum rail. */
export type FrameGateFacts = {
  met: boolean;
  done: number;
  total: number;
  /** Names the missing item(s), e.g. "Finance needs an SME". */
  remaining: string[];
};

/** The named programme goal, seeded from the Vision Cover Story headline (021). */
export type ProgrammeGoal = {
  headline: string;
  statedBy: string;
};

export type StreamFacts = {
  id: string;
  name: string;
  phase: StreamPhase;
  spineComplete: boolean;
  vsmComplete: boolean;
  openHighImpactValidationItems: number;
  analysed: boolean;
  waste?: number;
  potentialRoi?: number;
  /** 0..1 — confidence in this stream's facts. */
  confidence: number;
};

export type ProgrammeFacts = {
  visionCaptured: boolean;
  streams: StreamFacts[];
  /**
   * The captured Cover Story summary, when a vision exists (slice 9 feeds it).
   * `confirmed` gates the HEADLINE-as-goal (024): true → the goal; false → no
   * goal (the honest "not confirmed yet" state); ABSENT → back-compat (a goal —
   * the 023 demo seed + cockpit test depend on the legacy shape still yielding
   * the headline).
   */
  vision?: { coverStory: string; statedBy: string; confirmed?: boolean };
  /** Failure cases guarded (pre-mortem), when captured. */
  failureCases?: string[];
  // ── Org-context facts (022, FR-009/FR-010) — OPTIONAL; absent on first-run.
  // The dashboard derives (never stores) the org-setup warm-up to-do from these.
  /** The captured organisation, when one has been named. */
  organisation?: { name: string } | null;
  /** Whether the organisation's background has been confirmed by the human. */
  companyBackgroundConfirmed?: boolean;
  /** Legacy organisation pain points, retained for older snapshots and stream pain facts. */
  painPoints?: { id: string; title: string }[];
  // ── Scope facts (023, FR-004/FR-006) — OPTIONAL; absent on first-run.
  /** The "Areas of the business" in play; an area with >=1 SME unlocks mapping. */
  areas?: AreaFacts[];
  // ── Knowledge-repo link (032, FR-005/FR-006) — OPTIONAL; the unlinked floor
  // when absent. Drives the REQUIRED `setup-repo` item: done iff `linked`.
  /** The knowledge-repo link config; absent reads as the unlinked floor. */
  repoLink?: { status: 'unlinked' | 'linked'; owner?: string; org?: string; repo?: string };
};

export type StreamReadModel = {
  id: string;
  name: string;
  phase: StreamPhase;
  /** The §6 programme status, derived from phase + completion criteria. */
  status: StreamStatus;
  /** Human label for `status` ("In validation", "Signed off", …). */
  statusLabel: string;
  /** 0..1 — this stream's own structural completion against §6. */
  completion: number;
  /** false while open high-impact validation items exist (§6). */
  signedOff: boolean;
  /** open high-impact validation items blocking sign-off (§6). */
  openHighImpactValidationItems: number;
  confidence: number;
  /** false when the stream is not yet analysed. */
  valueSized: boolean;
  /** null === "not yet sized" (never 0/fabricated). */
  waste: number | null;
  potentialRoi: number | null;
};

/** A single derived next action (the dashboard's "recommended next" list). */
export type RecommendedAction = {
  id: string;
  title: string;
  detail: string;
  /** The ONE urgent item (rendered with the reserved coral accent). */
  urgent: boolean;
  actionLabel: string;
  route: string;
};

export type ProgrammeReadModel = {
  frameGateMet: boolean;
  /**
   * 0..1 — the honest cross-phase roll-up (023): phase-weighted across the five
   * cockpit phases, required-only, confidence-weighted for AI-derived
   * components. 0 when empty; never 1 on thin/low-confidence data; 1 reachable
   * with every REQUIRED item done and every OPTIONAL item untouched.
   */
  overallProgress: number;
  overallConfidence: number;
  /** Streams past "not started". */
  streamsInFlight: number;
  totalStreams: number;
  streams: StreamReadModel[];
  estate: { waste: number; potentialRoi: number; confidence: number };
  /** The captured vision summary, or null when none yet (Frame not met). */
  vision: VisionSummary | null;
  failureCases: string[];
  /** Derived, prioritised next moves — the single urgent item first. */
  recommendedNext: RecommendedAction[];
  /** The named goal (from the Vision Cover Story headline), or null when none. */
  goal: ProgrammeGoal | null;
  /**
   * True IFF a vision is captured but no CONFIRMED headline exists yet (024,
   * FR-007): the honest in-between distinct from both a confirmed goal and the
   * no-vision null. Drives the dashboard's "Confirm your Cover Story headline"
   * prompt WITHOUT elevating an unconfirmed headline as the goal. Optional so
   * existing literal read models (tests, COCKPIT_DEFAULTS) still type-check;
   * `deriveProgramme` always sets it.
   */
  goalsCapturedUnconfirmed?: boolean;
  /** The five cockpit phases in order, with required-only progress + lock state. */
  phases: PhaseReadModel[];
  /** The Frame-gate facts for the "unlock mapping" momentum rail. */
  frameGateFacts: FrameGateFacts;
  /** The exhaustive §6/§12 checklist bucketed by phase, with derived state. */
  checklist: ChecklistItem[];
  /** The captured areas (echoed input), or [] when none. */
  areas: AreaFacts[];
};

// The §6 phases in order. A stream's phase fixes a baseline completion; the
// completion criteria (spine/VSM complete, signed off) add the rest within
// the validate band so a fully-criteria-met stream reaches 1.0.
const PHASE_BASELINE: Record<StreamPhase, number> = {
  frame: 0,
  'as-is-capture': 0.1,
  analysis: 0.35,
  'to-be': 0.55,
  validate: 0.75,
};

const STATUS_LABEL: Record<StreamStatus, string> = {
  'not-started': 'Not started',
  'in-capture': 'In capture',
  'in-analysis': 'In analysis',
  'in-validation': 'In validation',
  'signed-off': 'Signed off',
};

function clamp01(n: number): number {
  if (Number.isNaN(n)) return 0;
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}

// A stream's own structural completion (0..1) — the phase baseline plus the
// §6 completion criteria. The criteria fill the band between the validate
// baseline and 1.0 so a stream that is in `validate`, has its spine and VSM
// complete and is signed off reads as fully complete.
function streamCompletion(stream: StreamFacts, signedOff: boolean): number {
  const baseline = PHASE_BASELINE[stream.phase] ?? 0;
  let bonus = 0;
  if (stream.spineComplete) bonus += 0.08;
  if (stream.vsmComplete) bonus += 0.08;
  if (signedOff) bonus += 0.09;
  return clamp01(baseline + bonus);
}

// The §6 programme status for a stream, derived from its phase + criteria.
function streamStatus(stream: StreamFacts, signedOff: boolean): StreamStatus {
  if (signedOff) return 'signed-off';
  if (stream.phase === 'validate') return 'in-validation';
  if (stream.phase === 'analysis' || stream.phase === 'to-be') return 'in-analysis';
  if (stream.phase === 'as-is-capture') return 'in-capture';
  return 'not-started';
}

// Org-context is COMPLETE iff an organisation is set AND its background is
// confirmed. Pain-point capture no longer gates the organisation profile.
function isOrgContextComplete(facts: ProgrammeFacts): boolean {
  return Boolean(facts.organisation && facts.companyBackgroundConfirmed === true);
}

// ── Scope (023) helpers ───────────────────────────────────────────────────

const areasOf = (facts: ProgrammeFacts): AreaFacts[] =>
  Array.isArray(facts.areas) ? facts.areas : [];

const hasSme = (a: AreaFacts): boolean => Array.isArray(a.smes) && a.smes.length >= 1;

// Echo the captured areas onto the read model WITHOUT materialising any
// stream/vsm/domain affordance (FR-011/FR-013) and WITHOUT inventing an absent
// optional one-liner (so equal input → deeply-equal output).
function projectAreas(facts: ProgrammeFacts): AreaFacts[] {
  return areasOf(facts).map((a) => {
    const projected: AreaFacts = {
      id: a.id,
      name: a.name,
      smes: (Array.isArray(a.smes) ? a.smes : []).map((s) =>
        s.contact === undefined ? { name: s.name } : { name: s.name, contact: s.contact },
      ),
    };
    if (a.oneLiner !== undefined) projected.oneLiner = a.oneLiner;
    return projected;
  });
}

// The named goal, seeded from the Vision Cover Story headline (FR-001). Null
// when no vision is captured — no goal is ever fabricated. The headline is
// GATED on confirmation (024, FR-007): an EXPLICIT `confirmed === false` yields
// null (the honest "not confirmed yet" state); a present-but-unconfirmed-FIELD
// vision (legacy, `confirmed` undefined) still yields the goal (back-compat for
// the 023 demo seed + cockpit test). Only an explicit `false` gates to null.
function deriveGoal(facts: ProgrammeFacts): ProgrammeGoal | null {
  if (!facts.visionCaptured || !facts.vision) return null;
  if (facts.vision.confirmed === false) return null;
  return { headline: facts.vision.coverStory, statedBy: facts.vision.statedBy };
}

// The distinct "goals captured but no confirmed headline" signal (024, FR-007):
// true IFF a vision is captured AND no goal headline is confirmed — the honest
// in-between, distinct from both a confirmed goal and the no-vision null.
function deriveGoalsCapturedUnconfirmed(
  facts: ProgrammeFacts,
  goal: ProgrammeGoal | null,
): boolean {
  return facts.visionCaptured === true && goal === null;
}

// The extended Frame gate (FR-004): vision captured AND >=1 area-with-an-SME.
// The legacy `visionCaptured && streams>=1` path is preserved (back-compat for
// the demo seed). An SME-less area does NOT satisfy it.
function deriveFrameGate(facts: ProgrammeFacts): { met: boolean; facts: FrameGateFacts } {
  const visionDone = facts.visionCaptured === true;
  const areas = areasOf(facts);
  const areaWithSme = areas.some(hasSme);
  const legacyStreamPath = facts.streams.length >= 1;
  const met = visionDone && (areaWithSme || legacyStreamPath);

  // The momentum rail always reports the two cockpit unlock items: a captured
  // vision and an area-with-an-SME. The remaining item names the gap precisely.
  const remaining: string[] = [];
  if (!visionDone) remaining.push('Capture the vision');
  if (!areaWithSme) {
    // Name an SME-less area when one exists; otherwise the generic gap.
    const orphan = areas.find((a) => !hasSme(a));
    remaining.push(orphan ? `${orphan.name} needs an SME` : 'Add an area with an SME');
  }

  if (met) {
    return { met, facts: { met, done: 2, total: 2, remaining: [] } };
  }
  const done = (visionDone ? 1 : 0) + (areaWithSme ? 1 : 0);
  return { met, facts: { met, done, total: 2, remaining } };
}

// A required-item doneness in [0,1]. The Map/Decide components stay
// confidence-weighted (completion × confidence) so thin data never reads 1.
type ItemDerivation = {
  id: string;
  phase: PhaseId;
  label: string;
  required: boolean;
  /** 0..1 fractional doneness (confidence-weighted for AI-derived items). */
  doneness: number;
  /** present when the item is blocked by a missing precondition. */
  blockedReason?: string;
};

// Average a per-stream confidence-weighted signal over the streams (0 when
// there are no streams — honest, never fabricated). `pick` returns 1 when the
// stream meets the criterion; the result is scaled by that stream's confidence
// so thin / low-confidence data never reads 1.
function streamSignal(
  streams: StreamReadModel[],
  facts: ProgrammeFacts,
  pick: (s: StreamReadModel, f: StreamFacts) => number,
): number {
  if (streams.length === 0) return 0;
  const byId = new Map(facts.streams.map((f) => [f.id, f]));
  return clamp01(
    streams.reduce((sum, s) => {
      const f = byId.get(s.id) as StreamFacts;
      return sum + clamp01(pick(s, f)) * s.confidence;
    }, 0) / streams.length,
  );
}

// The exhaustive §6/§12 activity list with each item's fractional doneness and
// any blocked reason — the single source the checklist + phase progress derive
// from. Pure: a function of the facts + derived streams only.
function deriveItems(facts: ProgrammeFacts, streams: StreamReadModel[]): ItemDerivation[] {
  const visionDone = facts.visionCaptured === true;
  // 029 Increment F: setup-vision is DONE on the CAPTURED vision (>=1 goal) — the
  // former `&& failureCasesDone` requirement is dropped (no reader supplies failure
  // cases, so it made a real engagement unreachably setup-incomplete). An
  // EXPLICITLY-unconfirmed cover story (`confirmed === false`, the honest "not
  // confirmed yet" state) does NOT complete the setup-vision item — it mirrors
  // `deriveGoal`'s confirmation gate so a pending headline keeps the engagement
  // setup-incomplete (the value board only leads once the headline is confirmed).
  const setupVisionDone = visionDone && facts.vision?.confirmed !== false;
  const areas = areasOf(facts);
  const areaWithSme = areas.some(hasSme);
  const orphanArea = areas.find((a) => !hasSme(a));
  const orgComplete = isOrgContextComplete(facts);

  const allSignedOff = streams.length >= 1 && streams.every((s) => s.signedOff);

  // The Frame "area with an SME" item is BLOCKED (not merely todo) when areas
  // exist but none carries an SME — named exactly so the rail can read it.
  const smeBlocked = areas.length >= 1 && !areaWithSme && orphanArea;

  // The REQUIRED knowledge-repo step (032, FR-005/FR-006): done IFF the repo is
  // `linked`; an absent/unlinked repoLink reads todo. No skip/defer state — its
  // only outcomes are done | todo (setup is never a locked/blocked phase here).
  const repoLinked = facts.repoLink?.status === 'linked';

  return [
    // ── Setup ────────────────────────────────────────────────────────────
    // The knowledge repository is FIRST and required (032).
    {
      id: 'setup-repo',
      phase: 'setup',
      label: 'Link the knowledge repository',
      required: true,
      doneness: repoLinked ? 1 : 0,
    },
    {
      id: 'setup-org',
      phase: 'setup',
      label: 'Set the organisation & company context',
      required: true,
      doneness: orgComplete ? 1 : 0,
    },
    {
      id: 'setup-vision',
      phase: 'setup',
      label: 'Capture the vision & failure cases',
      required: true,
      doneness: setupVisionDone ? 1 : 0,
    },
    {
      id: 'setup-areas',
      phase: 'setup',
      label: 'Map the areas of the business + assign SMEs',
      required: true,
      doneness: areaWithSme ? 1 : 0,
    },
    // ── Frame · gate ─────────────────────────────────────────────────────
    {
      id: 'frame-vision',
      phase: 'frame',
      label: 'Vision captured',
      required: true,
      doneness: visionDone ? 1 : 0,
    },
    {
      id: 'frame-sme',
      phase: 'frame',
      label: 'At least one area with an SME',
      required: true,
      doneness: areaWithSme ? 1 : 0,
      blockedReason: smeBlocked ? `${orphanArea!.name} needs an SME` : undefined,
    },
    // ── Map (As-is capture + Analysis, confidence-weighted) ──────────────
    {
      id: 'map-spine',
      phase: 'map',
      label: 'Capture the spine — the value streams in each area',
      required: true,
      doneness: streamSignal(streams, facts, (_s, f) => (f.spineComplete ? 1 : 0)),
    },
    {
      id: 'map-vsm',
      phase: 'map',
      label: 'Value stream maps + systems touched',
      required: true,
      doneness: streamSignal(streams, facts, (_s, f) => (f.vsmComplete ? 1 : 0)),
    },
    {
      id: 'map-step-detail',
      phase: 'map',
      label: 'Step detail & provenance',
      required: true,
      doneness: streamSignal(streams, facts, (_s, f) => (f.analysed ? 1 : 0)),
    },
    {
      id: 'map-domain',
      phase: 'map',
      label: 'Domain grouping (architect, cross-stream)',
      required: false,
      doneness: 0,
    },
    // ── Decide (Validate + Business case + Prioritise) ───────────────────
    {
      id: 'decide-validation',
      phase: 'decide',
      label: 'Validation queue',
      required: true,
      doneness: streamSignal(streams, facts, (s) => (s.signedOff ? 1 : 0)),
    },
    {
      id: 'decide-case',
      phase: 'decide',
      label: 'Business case',
      required: true,
      doneness: streamSignal(streams, facts, (s) => (s.valueSized ? 1 : 0)),
    },
    {
      id: 'decide-prioritisation',
      phase: 'decide',
      label: 'Prioritisation',
      required: true,
      doneness: streamSignal(streams, facts, (s) => (s.valueSized && s.signedOff ? 1 : 0)),
    },
    // ── Hand over (Golden thread + emit specs) ───────────────────────────
    // The optional "+ later" item is FIRST so the read model's first handover
    // item is the optional one; a required hand-over item follows so the phase
    // carries a required count (mock: "Hand over · 0 / 1").
    {
      id: 'handover-golden-thread',
      phase: 'handover',
      label: 'Golden thread + emit specifications to the target repository',
      required: false,
      doneness: 0,
    },
    {
      id: 'handover-model',
      phase: 'handover',
      label: 'Hand over the validated process model',
      required: true,
      doneness: allSignedOff ? 1 : 0,
    },
  ];
}

// The phase order is fixed and load-bearing (the contract pins it exactly).
const PHASE_ORDER: { id: PhaseId; label: string }[] = [
  { id: 'setup', label: 'Setup' },
  { id: 'frame', label: 'Frame' },
  { id: 'map', label: 'Map' },
  { id: 'decide', label: 'Decide' },
  { id: 'handover', label: 'Hand over' },
];

// Derive the five-phase model + the checklist together (they share the item
// derivation). Map/Decide/Handover lock while the Frame gate is unmet; setup
// and frame never lock. Phase progress is required-only and confidence-weighted.
function derivePhasesAndChecklist(
  items: ItemDerivation[],
  frameGateMet: boolean,
): { phases: PhaseReadModel[]; checklist: ChecklistItem[] } {
  const checklist: ChecklistItem[] = [];
  const phases: PhaseReadModel[] = PHASE_ORDER.map(({ id, label }) => {
    const locked = (id === 'map' || id === 'decide' || id === 'handover') && !frameGateMet;
    const phaseItems = items.filter((i) => i.phase === id);
    const requiredItems = phaseItems.filter((i) => i.required);
    const requiredTotal = requiredItems.length;
    const requiredDone = requiredItems.filter((i) => i.doneness >= 1).length;
    const progress =
      requiredTotal === 0
        ? 1
        : clamp01(requiredItems.reduce((sum, i) => sum + i.doneness, 0) / requiredTotal);

    for (const item of phaseItems) {
      let state: ChecklistState;
      let blockedReason: string | undefined;
      if (locked) {
        state = 'locked';
      } else if (item.blockedReason) {
        state = 'blocked';
        blockedReason = item.blockedReason;
      } else if (item.doneness >= 1) {
        state = 'done';
      } else if (item.doneness > 0) {
        state = 'in-progress';
      } else {
        state = 'todo';
      }
      const entry: ChecklistItem = {
        id: item.id,
        phase: item.phase,
        label: item.label,
        required: item.required,
        state,
      };
      if (blockedReason) entry.blockedReason = blockedReason;
      checklist.push(entry);
    }

    return { id, label, requiredDone, requiredTotal, progress, locked };
  });

  return { phases, checklist };
}

// Derive the prioritised "recommended next" list from the programme facts —
// the single URGENT item (open high-impact validation) first, then the next
// highest-value moves, capped at three. Honest: it only surfaces actions the
// facts justify (it is empty when there is nothing to do).
function deriveRecommendedNext(
  facts: ProgrammeFacts,
  streams: StreamReadModel[],
): RecommendedAction[] {
  const actions: RecommendedAction[] = [];

  // The one urgent item: open high-impact validation blocking sign-off.
  const openHighImpact = facts.streams.reduce(
    (n, s) => n + Math.max(0, s.openHighImpactValidationItems),
    0,
  );
  if (openHighImpact > 0) {
    const blocked = streams.find((s) => s.openHighImpactValidationItems > 0);
    actions.push({
      id: 'validation',
      title: `${openHighImpact} high-impact fact${openHighImpact > 1 ? 's' : ''} need validation`,
      detail: blocked ? `Blocks sign-off on the ${blocked.name} stream` : 'Blocks stream sign-off',
      urgent: true,
      actionLabel: 'Resolve now',
      route: '/validation',
    });
  }

  // The "Assign an SME" momentum CTA (023, FR-005): when a vision is captured
  // but the ONLY thing keeping the Frame gate shut is an SME-less area, name
  // the exact area to unlock mapping. This is the single reserved-coral move,
  // so it is urgent ONLY when no validation item already claimed urgency
  // (exactly one urgent item). It routes to the scope surface, never a stream.
  const areas = areasOf(facts);
  const areaWithSme = areas.some(hasSme);
  const orphanArea = areas.find((a) => !hasSme(a));
  const smeIsTheGap = facts.visionCaptured === true && !areaWithSme && orphanArea;
  if (smeIsTheGap) {
    actions.push({
      id: 'assign-sme',
      title: `Assign an SME to ${orphanArea!.name}`,
      detail: 'Unlocks mapping for this area',
      urgent: openHighImpact === 0,
      actionLabel: 'Assign SME',
      route: '/',
    });
  }

  // The org-setup warm-up (022, FR-009/FR-010): when org-context is INCOMPLETE
  // (no org OR background unconfirmed OR no pain point — including the first-run
  // default where the facts are absent), surface a NON-URGENT "Set up the
  // organisation" action. Ordered immediately after the one urgent item and
  // BEFORE the spine/case items so on first-run it leads the get-started
  // sequence. Pure projection: derived from the facts, never stored.
  if (!isOrgContextComplete(facts)) {
    actions.push({
      id: 'org-setup',
      title: 'Set up the organisation',
      detail: 'Name the client and confirm their background',
      urgent: false,
      actionLabel: 'Set up',
      route: '/organisation',
    });
  }

  // Not-started streams: capture the spine.
  streams
    .filter((s) => s.status === 'not-started')
    .forEach((s) => {
      actions.push({
        id: `spine-${s.id}`,
        title: `Capture the spine for ${s.name}`,
        detail: 'Not started · empty shell ready',
        urgent: false,
        actionLabel: 'Start',
        route: `/streams/${s.id}/spine`,
      });
    });

  // In-flight but not-yet-sized streams: add business-case figures.
  streams
    .filter((s) => s.status !== 'not-started' && !s.valueSized)
    .forEach((s) => {
      actions.push({
        id: `case-${s.id}`,
        title: `Add business-case figures to ${s.name}`,
        detail: 'Sizes the prize · feeds ROI',
        urgent: false,
        actionLabel: 'Open',
        route: `/streams/${s.id}/business-case`,
      });
    });

  return actions.slice(0, 3);
}

export function deriveProgramme(facts: ProgrammeFacts): ProgrammeReadModel {
  // The extended Frame gate (023, FR-004): vision + an area-with-an-SME OR the
  // legacy vision + >=1 stream path (back-compat for the demo seed).
  const gate = deriveFrameGate(facts);
  const frameGateMet = gate.met;

  const streams: StreamReadModel[] = facts.streams.map((s) => {
    // §6: a stream is NOT signed off while it has open high-impact items.
    const signedOff = s.openHighImpactValidationItems <= 0 && s.spineComplete && s.vsmComplete;
    const completion = streamCompletion(s, signedOff);
    const status = streamStatus(s, signedOff);
    const confidence = clamp01(s.confidence);
    const valueSized = s.analysed === true;
    return {
      id: s.id,
      name: s.name,
      phase: s.phase,
      status,
      statusLabel: STATUS_LABEL[status],
      completion,
      signedOff,
      openHighImpactValidationItems: Math.max(0, s.openHighImpactValidationItems),
      confidence,
      valueSized,
      // Un-analysed streams read "not yet sized" (null), never 0/fabricated.
      waste: valueSized ? s.waste ?? 0 : null,
      potentialRoi: valueSized ? s.potentialRoi ?? 0 : null,
    };
  });

  // The honest cross-phase roll-up (023, FR-002/FR-003): derive the exhaustive
  // §6/§12 item list, then the five-phase model + checklist. `overallProgress`
  // is the AVERAGE of the five phases' required-only progress (each Map/Decide
  // component stays confidence-weighted). Empty → exactly 0; every REQUIRED
  // item done at full confidence (every OPTIONAL untouched) → exactly 1; thin /
  // low-confidence data never reads 1; lowering a stream's confidence lowers it.
  const items = deriveItems(facts, streams);
  const { phases, checklist } = derivePhasesAndChecklist(items, frameGateMet);
  const overallProgress = clamp01(
    phases.reduce((sum, p) => sum + p.progress, 0) / phases.length,
  );

  const overallConfidence = streams.length
    ? clamp01(streams.reduce((sum, s) => sum + s.confidence, 0) / streams.length)
    : 0;

  // Estate value roll-up: the SUM of the per-stream SIZED figures only.
  // Un-analysed streams contribute nothing (no 0-padding, no fabrication).
  const sized = streams.filter((s) => s.valueSized);
  const estateWaste = sized.reduce((sum, s) => sum + (s.waste ?? 0), 0);
  const estateRoi = sized.reduce((sum, s) => sum + (s.potentialRoi ?? 0), 0);
  const estateConfidence = sized.length
    ? clamp01(sized.reduce((sum, s) => sum + s.confidence, 0) / sized.length)
    : 0;

  const streamsInFlight = streams.filter((s) => s.status !== 'not-started').length;

  const goal = deriveGoal(facts);

  return {
    frameGateMet,
    overallProgress,
    overallConfidence,
    streamsInFlight,
    totalStreams: streams.length,
    streams,
    estate: { waste: estateWaste, potentialRoi: estateRoi, confidence: estateConfidence },
    vision: facts.vision ?? null,
    failureCases: facts.failureCases ? [...facts.failureCases] : [],
    recommendedNext: deriveRecommendedNext(facts, streams),
    goal,
    goalsCapturedUnconfirmed: deriveGoalsCapturedUnconfirmed(facts, goal),
    phases,
    frameGateFacts: gate.facts,
    checklist,
    areas: projectAreas(facts),
  };
}

// ══════════════════════════════════════════════════════════════════════
// 029-value-proven-dashboard · Increment A — the value-proven projection.
//
// `deriveValueProven` is a SEPARATE pure projection (NOT an extension of
// `deriveProgramme`'s return) that backs the five-element value-proven board:
// three headline measures, three value bands, value-stream rows, an attention
// list and a calibration series. Each figure carries its provenance.
//
// REAL vs ILLUSTRATIVE is STRUCTURAL (the honesty contract, FR-001/FR-004):
//   • REAL (derived from the fact set the rest of the model reads):
//       - progress-to-vision  ← visionGoals × confidence (reuses the existing
//         clamp/confidence-weighting discipline; never 100% on thin data; 0 on
//         empty; lowers when confidence lowers). FR-005/SC-004.
//       - the value-stream rows' id/name ← facts.areas.
//   • ILLUSTRATIVE (no real source by design — E2–E6 unbuilt): forecast-accuracy,
//     value-realisation, the expected/speculative band money + assumptions, every
//     loop column, and the calibration series — ALL from the labelled fixture
//     `illustrative.ts` (static literals). FR-006/FR-007/FR-009/FR-011.
//
// PURE/DETERMINISTIC (SC-007): a function of `facts` + the static fixture only —
// no I/O, no fetch, no Math.random/Date.now; `facts` is never mutated; equal
// input → deeply-equal output.
// ══════════════════════════════════════════════════════════════════════

/** A REAL vision goal with the confidence we hold in it (0..1). */
export type VisionGoalFact = { id: string; text: string; confidence: number };

/** The existing fact set PLUS the real vision goals that drive progress-to-vision. */
export type ValueProvenFacts = ProgrammeFacts & { visionGoals?: VisionGoalFact[] };

export type HeadlineMeasureKind = 'progress' | 'trend' | 'ratio';

/** A headline measure (north-star). Progress-to-vision REAL; the other two illustrative. */
export type HeadlineMeasure = {
  id: 'progress-to-vision' | 'forecast-accuracy' | 'value-realisation';
  label: string;
  kind: HeadlineMeasureKind;
  /** progress-to-vision is 0..100 (a percentage). */
  value: number;
  /** 0..1, present on progress-to-vision. */
  confidence?: number;
  provenance: Provenance;
};

/** A value band. Proven is REAL-empty (money null); expected/speculative illustrative. */
export type ValueBand = {
  id: 'proven' | 'expected' | 'speculative';
  label: string;
  /** proven: null (honest "none yet"); expected/speculative: a number. */
  money: number | null;
  provenance: Provenance;
  /** A non-empty traceable-assumption string. */
  assumption: string;
};

/** The loop columns for a value-stream row — all illustrative, each nullable. */
export type ValueStreamColumns = {
  stage: string | null;
  baseline: number | null;
  predictedRange: string | null;
  delivery: string | null;
  realised: number | null;
  adoption: number | null;
};

/** A value-stream row: REAL id/name (from an area), illustrative loop columns. */
export type ValueStreamRow = {
  id: string;
  name: string;
  /** EXACTLY one row across the set is the calibration wedge. */
  isWedge: boolean;
  provenance: 'illustrative';
  columns: ValueStreamColumns;
};

/** An attention item — adapted from RecommendedAction with provenance + a confirm route. */
export type AttentionItem = {
  id: string;
  title: string;
  detail: string;
  urgent: boolean;
  provenance: Provenance;
  confirmRoute: string;
};

/** An illustrative calibration point (predicted vs realised over time). */
export type CalibrationPoint = { predicted: number; realised: number; at: string };

export type ValueProvenModel = {
  /** EXACTLY three, in north-star order. */
  measures: HeadlineMeasure[];
  /** EXACTLY three: proven, expected, speculative. NO total/aggregate field. */
  bands: ValueBand[];
  /** One per input area (REAL id/name). */
  streamRows: ValueStreamRow[];
  attention: AttentionItem[];
  /** Illustrative series (from the fixture). */
  calibration: CalibrationPoint[];
};

// Progress-to-vision (REAL, FR-005/SC-004): the confidence-weighted reach across
// the captured vision goals, as a 0..100 percentage. REUSES the existing
// clamp/confidence-averaging discipline (the same shape as `streamSignal` /
// `overallConfidence`): the mean of the per-goal confidences, clamped to [0,1].
// Thin / partial-confidence goals therefore read < 100; empty reads 0; lowering
// any goal's confidence lowers the figure. No fabrication, never 100 on thin data.
function deriveProgressToVision(goals: VisionGoalFact[]): { value: number; confidence: number } {
  if (goals.length === 0) return { value: 0, confidence: 0 };
  const confidence = clamp01(
    goals.reduce((sum, g) => sum + clamp01(g.confidence), 0) / goals.length,
  );
  return { value: confidence * 100, confidence };
}

// Build a ValueBand whose `assumption` (the FR-008 traceable-assumption string)
// is a NORMAL enumerable own property: readable + typed, and visible to
// `Object.keys`/spread/`JSON.stringify` like any other field. The SC-002
// "no aggregate/total/sum band field" guard is now exact-token (whole-key)
// matching, so a plain `assumption` field passes. Deterministic: a function of
// its inputs (static literals) only; two calls with equal inputs are
// deeply-equal.
function makeBand(
  base: { id: ValueBand['id']; label: string; money: number | null; provenance: Provenance },
  assumption: string,
): ValueBand {
  return { ...base, assumption };
}

export function deriveValueProven(facts: ValueProvenFacts): ValueProvenModel {
  const goals = Array.isArray(facts.visionGoals) ? facts.visionGoals : [];
  const ptv = deriveProgressToVision(goals);

  // ── Three headline measures, north-star order. progress-to-vision is REAL
  // (provenance 'inferred' — derived from the captured goals, NOT illustrative);
  // the other two are illustrative figures from the fixture (no real source).
  const measures: HeadlineMeasure[] = [
    {
      id: 'progress-to-vision',
      label: 'Progress to vision',
      kind: 'progress',
      value: ptv.value,
      confidence: ptv.confidence,
      provenance: 'inferred',
    },
    {
      id: 'forecast-accuracy',
      label: 'Forecast accuracy',
      kind: 'trend',
      value: ILLUSTRATIVE_FORECAST_ACCURACY,
      provenance: 'illustrative',
    },
    {
      id: 'value-realisation',
      label: 'Value realisation',
      kind: 'ratio',
      value: ILLUSTRATIVE_VALUE_REALISATION,
      provenance: 'illustrative',
    },
  ];

  // ── Three value bands. Proven is REAL-empty (money null, honest "none yet" —
  // no E2–E6 measurement exists), NOT illustrative; expected/speculative are
  // illustrative figures + assumptions from the fixture. NO total anywhere.
  //
  // NB (the never-summed guard): `assumption` is a normal enumerable field
  // carrying the contract's traceable-assumption string (FR-008). The SC-002
  // "no aggregate/total/sum band field" guard uses WHOLE-KEY matching
  // (`total|sum|aggregate|combined|overall`), so `assumption` is allowed; the
  // honest "three separate figures, never one total" contract holds because no
  // aggregate key or summed figure exists, not by hiding any field.
  const bands: ValueBand[] = [
    makeBand(
      { id: 'proven', label: 'Proven', money: null, provenance: 'inferred' },
      'No measured value banked yet — the delivery loop has not run.',
    ),
    makeBand(
      { id: 'expected', label: 'Expected', money: ILLUSTRATIVE_EXPECTED_MONEY, provenance: 'illustrative' },
      ILLUSTRATIVE_EXPECTED_ASSUMPTION,
    ),
    makeBand(
      {
        id: 'speculative',
        label: 'Speculative',
        money: ILLUSTRATIVE_SPECULATIVE_MONEY,
        provenance: 'illustrative',
      },
      ILLUSTRATIVE_SPECULATIVE_ASSUMPTION,
    ),
  ];

  // ── One value-stream row per REAL area (id/name real; loop columns illustrative
  // from the fixture). EXACTLY one row is the calibration wedge (the first area).
  const areas = areasOf(facts);
  const streamRows: ValueStreamRow[] = areas.map((a, i) => ({
    id: a.id,
    name: a.name,
    isWedge: i === 0,
    provenance: 'illustrative',
    columns: { ...ILLUSTRATIVE_STREAM_COLUMNS },
  }));

  // ── Attention list: REAL items adapted from the existing recommendedNext
  // machinery (so the dashboard reuses the one prioritiser), re-shaped with a
  // provenance + a confirmRoute. These are derived from the facts (REAL), not
  // illustrative.
  const programme = deriveProgramme(facts);
  const attention: AttentionItem[] = programme.recommendedNext.map((a) => ({
    id: a.id,
    title: a.title,
    detail: a.detail,
    urgent: a.urgent,
    provenance: 'inferred',
    confirmRoute: a.route,
  }));

  // ── Calibration series (illustrative, from the fixture). Present even with
  // empty real facts (it has no real source by design).
  const calibration: CalibrationPoint[] = ILLUSTRATIVE_CALIBRATION.map((p) => ({ ...p }));

  return { measures, bands, streamRows, attention, calibration };
}
